import { useSyncExternalStore } from 'react';
import { Vec3 } from '../geometry';
import { FramingPreset, PTZValues } from '../types';

// The virtual target of a fixed-frame preset, shared by the target section
// under the player, the rink (sets x/y), the video overlay (drags z) and the
// clip's Set Position menu. Module-scoped like usePTZStore: there is one
// preview per page.

/** The Fixed Camera Clip that opened target mode, and how to frame it. */
export interface TargetSession {
  /** Identifies the clip's controls, so only they can end their own session. */
  owner: object;
  /**
   * Sets the clip's position; `framingPresetId` when it comes from a preset.
   * Resolves false when the save failed (the clip says why).
   */
  apply: (ptz: PTZValues, framingPresetId?: number) => Promise<boolean>;
  /** The preview's PTZ right now (throws while it isn't known yet). */
  readCurrentPTZ: () => PTZValues;
}

export interface FramingTargetState {
  /**
   * Target mode is on: a Fixed Camera Clip asked to aim at a spot, so the
   * panel under the player shows the target section and the rink and the
   * video aim the camera (in Manual). null everywhere else.
   */
  session: TargetSession | null;
  target: Vec3 | null;
  /** The preset the target came from, until it is moved. */
  preset: Pick<FramingPreset, 'id' | 'name'> | null;
  /** The target dot is being dragged on the video; the camera holds still meanwhile. */
  dragging: boolean;
  /** The target was let go because someone steered the camera by hand. */
  released: boolean;
  /** The clip was framed on the target; `name` is the preset's, if any. */
  applied: { name: string | null } | null;
  /**
   * The camera stopped short of the target: the player keeps the frame on the
   * sensor, so it can't look that high at this zoom.
   */
  unreachable: boolean;
  /**
   * A clip position save is in flight. Kept across sessions, so applying
   * again can't race it from a reopened target mode.
   */
  saving: boolean;
}

/** Highest a target can be raised, in feet: well above any scoreboard. */
export const MAX_TARGET_HEIGHT = 80;
/** Above this (feet), a target the camera can't frame is too high for the zoom. */
export const HIGH_TARGET_FT = 3;

const CLEARED = {
  target: null,
  preset: null,
  dragging: false,
  released: false,
  applied: null,
  unreachable: false,
};

let state: FramingTargetState = { session: null, saving: false, ...CLEARED };
const listeners = new Set<() => void>();

const set = (patch: Partial<FramingTargetState>) => {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
};

const clampHeight = (z: number) => Math.min(Math.max(z, 0), MAX_TARGET_HEIGHT);

export const framingTarget = {
  get: () => state,
  /** Enters target mode for a clip, from scratch. */
  begin: (session: TargetSession) => set({ session, ...CLEARED }),
  /** Leaves target mode (Done, Cancel, Auto-ISO). */
  end: () => {
    if (state.session) set({ session: null, ...CLEARED });
  },
  /** Leaves target mode if `owner` opened it: its clip was closed. */
  endFor: (owner: object) => {
    if (state.session?.owner === owner) framingTarget.end();
  },
  /** Puts the target at a surface point, keeping its height. */
  place: (x: number, y: number) => set({ ...CLEARED, target: { x, y, z: state.target?.z ?? 0 } }),
  setHeight: (z: number) =>
    state.target &&
    set({
      target: { ...state.target, z: clampHeight(z) },
      preset: null,
      applied: null,
      unreachable: false,
    }),
  setDragging: (dragging: boolean) => set({ dragging }),
  setUnreachable: (unreachable: boolean) => set({ unreachable }),
  loadPreset: (preset: Pick<FramingPreset, 'id' | 'name' | 'target'>) =>
    set({ ...CLEARED, target: { ...preset.target }, preset: { id: preset.id, name: preset.name } }),
  /**
   * Sets the clip's position through `session`, one save at a time: resolves
   * false without saving while another one is in flight.
   */
  applyTo: async (session: TargetSession, ptz: PTZValues, framingPresetId?: number) => {
    if (state.saving) return false;
    set({ saving: true });
    try {
      return await session.apply(ptz, framingPresetId);
    } finally {
      set({ saving: false });
    }
  },
  /** The clip now aims at the target. */
  markApplied: (name: string | null) => set({ applied: { name } }),
  /**
   * Still the session and spot a save started from. Back, another clip or a
   * moved spot while it was pending make its result stale.
   */
  isCurrent: (session: TargetSession, target: Vec3 | null) =>
    state.session === session && state.target === target,
  /** Back to step one, to aim somewhere else. */
  restart: () => set(CLEARED),
  /**
   * The camera was steered by hand: the frame no longer shows the target. An
   * applied clip is done with target mode; otherwise the section asks for a
   * new spot.
   */
  release: () => {
    if (state.applied) framingTarget.end();
    else if (state.target) set({ ...CLEARED, released: true });
  },
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useFramingTarget = () => useSyncExternalStore(subscribe, framingTarget.get);
